import React, { Component } from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Cell } from 'recharts';
const ImportExportTrackerDashboard: React.FC = () => {
  const kpis = [{
    title: 'Import Spend',
    value: '₹2,850 Cr',
    change: '+15.2%',
    isPositive: false,
    subtitle: 'YTD imports'
  }, {
    title: 'Export Revenue',
    value: '₹1,425 Cr',
    change: '+8.5%',
    isPositive: true,
    subtitle: 'YTD exports'
  }, {
    title: 'FX Exposure',
    value: '42.8%',
    change: '+3.2%',
    isPositive: false,
    subtitle: 'Foreign currency exposure'
  }];
  // Import vs Export Trend Data
  const importExportTrendData = [{
    month: 'Jan',
    imports: 285,
    exports: 142,
    netTrade: -143
  }, {
    month: 'Feb',
    imports: 298,
    exports: 148,
    netTrade: -150
  }, {
    month: 'Mar',
    imports: 312,
    exports: 155,
    netTrade: -157
  }, {
    month: 'Apr',
    imports: 295,
    exports: 138,
    netTrade: -157
  }, {
    month: 'May',
    imports: 325,
    exports: 165,
    netTrade: -160
  }, {
    month: 'Jun',
    imports: 338,
    exports: 172,
    netTrade: -166
  }, {
    month: 'Jul',
    imports: 342,
    exports: 168,
    netTrade: -174
  }, {
    month: 'Aug',
    imports: 355,
    exports: 178,
    netTrade: -177
  }, {
    month: 'Sep',
    imports: 348,
    exports: 175,
    netTrade: -173
  }];
  // Country-wise Trade Data
  const countryTradeData = [{
    country: 'China',
    imports: 1245,
    exports: 285,
    netTrade: -960,
    color: '#ef4444'
  }, {
    country: 'Germany',
    imports: 485,
    exports: 325,
    netTrade: -160,
    color: '#f59e0b'
  }, {
    country: 'Japan',
    imports: 425,
    exports: 245,
    netTrade: -180,
    color: '#f59e0b'
  }, {
    country: 'USA',
    imports: 285,
    exports: 385,
    netTrade: 100,
    color: '#10b981'
  }, {
    country: 'South Korea',
    imports: 245,
    exports: 125,
    netTrade: -120,
    color: '#f59e0b'
  }, {
    country: 'UK',
    imports: 85,
    exports: 185,
    netTrade: 100,
    color: '#10b981'
  }, {
    country: 'Thailand',
    imports: 125,
    exports: 95,
    netTrade: -30,
    color: '#f59e0b'
  }, {
    country: 'Taiwan',
    imports: 165,
    exports: 75,
    netTrade: -90,
    color: '#f59e0b'
  }];
  // FX Impact Chart Data
  const fxImpactData = [{
    month: 'Jan',
    usdRate: 82.5,
    eurRate: 89.2,
    jpyRate: 0.62,
    fxGainLoss: -12.5
  }, {
    month: 'Feb',
    usdRate: 82.8,
    eurRate: 89.8,
    jpyRate: 0.63,
    fxGainLoss: -15.2
  }, {
    month: 'Mar',
    usdRate: 83.2,
    eurRate: 90.5,
    jpyRate: 0.64,
    fxGainLoss: -18.5
  }, {
    month: 'Apr',
    usdRate: 83.5,
    eurRate: 91.2,
    jpyRate: 0.65,
    fxGainLoss: -16.8
  }, {
    month: 'May',
    usdRate: 83.8,
    eurRate: 91.8,
    jpyRate: 0.66,
    fxGainLoss: -22.5
  }, {
    month: 'Jun',
    usdRate: 84.2,
    eurRate: 92.5,
    jpyRate: 0.67,
    fxGainLoss: -25.8
  }, {
    month: 'Jul',
    usdRate: 84.5,
    eurRate: 93.2,
    jpyRate: 0.68,
    fxGainLoss: -28.2
  }, {
    month: 'Aug',
    usdRate: 84.8,
    eurRate: 93.8,
    jpyRate: 0.69,
    fxGainLoss: -32.5
  }, {
    month: 'Sep',
    usdRate: 83.9,
    eurRate: 92.5,
    jpyRate: 0.67,
    fxGainLoss: -28.8
  }];
  // Import/Export SKU/Commodity Listing Table
  const importExportListingData = [{
    skuCode: 'IMP-ECU-2024',
    description: 'Electronic Control Units',
    type: 'Import',
    category: 'Electronics',
    country: 'China',
    supplier: 'Bosch China',
    currency: 'USD',
    unitPrice: 285,
    volume: 12500,
    value: 356.25,
    fxRate: 82.5,
    inrValue: 293.9,
    leadTime: 45,
    hscode: '8537.10.90',
    dutyRate: 10.0,
    customsDuty: 29.4,
    totalCost: 323.3
  }, {
    skuCode: 'IMP-BATT-SYS',
    description: 'Lithium-ion Battery Cells',
    type: 'Import',
    category: 'Batteries',
    country: 'South Korea',
    supplier: 'LG Energy',
    currency: 'USD',
    unitPrice: 425,
    volume: 8500,
    value: 361.25,
    fxRate: 82.5,
    inrValue: 298.0,
    leadTime: 52,
    hscode: '8507.60.00',
    dutyRate: 5.0,
    customsDuty: 14.9,
    totalCost: 312.9
  }, {
    skuCode: 'IMP-LED-MOD',
    description: 'LED Headlamp Modules',
    type: 'Import',
    category: 'Lighting',
    country: 'Germany',
    supplier: 'Hella GmbH',
    currency: 'EUR',
    unitPrice: 185,
    volume: 15200,
    value: 281.2,
    fxRate: 89.2,
    inrValue: 250.8,
    leadTime: 38,
    hscode: '8512.20.10',
    dutyRate: 15.0,
    customsDuty: 37.6,
    totalCost: 288.4
  }, {
    skuCode: 'IMP-SENS-PKG',
    description: 'Radar Sensor Package',
    type: 'Import',
    category: 'Sensors',
    country: 'Japan',
    supplier: 'Denso Corp',
    currency: 'JPY',
    unitPrice: 42500,
    volume: 6800,
    value: 289.0,
    fxRate: 0.62,
    inrValue: 179.2,
    leadTime: 42,
    hscode: '8526.91.00',
    dutyRate: 10.0,
    customsDuty: 17.9,
    totalCost: 197.1
  }, {
    skuCode: 'IMP-CHIP-SET',
    description: 'Automotive Chipset',
    type: 'Import',
    category: 'Electronics',
    country: 'Taiwan',
    supplier: 'TSMC',
    currency: 'USD',
    unitPrice: 125,
    volume: 28500,
    value: 356.25,
    fxRate: 82.5,
    inrValue: 293.9,
    leadTime: 55,
    hscode: '8542.31.10',
    dutyRate: 0.0,
    customsDuty: 0.0,
    totalCost: 293.9
  }, {
    skuCode: 'EXP-GEAR-ASM',
    description: 'Transmission Gear Assembly',
    type: 'Export',
    category: 'Drivetrain',
    country: 'USA',
    customer: 'Ford Motors',
    currency: 'USD',
    unitPrice: 485,
    volume: 5200,
    value: 252.2,
    fxRate: 82.5,
    inrValue: 208.1,
    leadTime: 28,
    hscode: '8708.40.10',
    dutyRate: 0.0,
    customsDuty: 0.0,
    totalCost: 208.1
  }, {
    skuCode: 'EXP-FORGE-PT',
    description: 'Forged Engine Parts',
    type: 'Export',
    category: 'Engine',
    country: 'Germany',
    customer: 'BMW Group',
    currency: 'EUR',
    unitPrice: 325,
    volume: 4800,
    value: 156.0,
    fxRate: 89.2,
    inrValue: 139.2,
    leadTime: 32,
    hscode: '8409.91.10',
    dutyRate: 0.0,
    customsDuty: 0.0,
    totalCost: 139.2
  }, {
    skuCode: 'EXP-CAST-BRK',
    description: 'Cast Brake Components',
    type: 'Export',
    category: 'Braking',
    country: 'UK',
    customer: 'Jaguar Land Rover',
    currency: 'GBP',
    unitPrice: 225,
    volume: 6500,
    value: 146.25,
    fxRate: 102.5,
    inrValue: 142.7,
    leadTime: 25,
    hscode: '8708.30.10',
    dutyRate: 0.0,
    customsDuty: 0.0,
    totalCost: 142.7
  }, {
    skuCode: 'IMP-TURBO-CH',
    description: 'Turbocharger Assembly',
    type: 'Import',
    category: 'Engine',
    country: 'Germany',
    supplier: 'BorgWarner',
    currency: 'EUR',
    unitPrice: 685,
    volume: 3200,
    value: 219.2,
    fxRate: 89.2,
    inrValue: 195.5,
    leadTime: 48,
    hscode: '8414.80.11',
    dutyRate: 7.5,
    customsDuty: 14.7,
    totalCost: 210.2
  }, {
    skuCode: 'EXP-STEER-SYS',
    description: 'Steering System Assembly',
    type: 'Export',
    category: 'Steering',
    country: 'USA',
    customer: 'GM Motors',
    currency: 'USD',
    unitPrice: 545,
    volume: 3800,
    value: 207.1,
    fxRate: 82.5,
    inrValue: 170.9,
    leadTime: 30,
    hscode: '8708.94.10',
    dutyRate: 0.0,
    customsDuty: 0.0,
    totalCost: 170.9
  }];
  const getTypeColor = (type: string) => {
    return type === 'Import' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700';
  };
  return <div data-id="element-9238">
      <DescriptionPanel title="Import/Export Tracker & FX Analysis" description="Comprehensive tracking of international trade activities, foreign exchange exposure, and cross-border transactions. Monitor import spend, export revenue, FX impact, and country-wise trade patterns to optimize global supply chain operations and manage currency risks." insights={['Import spend at ₹2,850 Cr YTD - up 15.2% driven by electronics and battery imports', 'Export revenue at ₹1,425 Cr YTD - up 8.5% with strong growth in USA and UK markets', 'FX exposure at 42.8% - cumulative FX loss of ₹28.8 Cr due to rupee depreciation', 'China remains largest import partner at ₹1,245 Cr, USA leading export destination at ₹385 Cr']} data-id="element-9239" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-9240">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-9241">
          Import/Export Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-9242">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-9243">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-9244">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-9245">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-9246">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-9247">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-9248" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-9249" />}
                {kpi.change} vs last year
              </div>
            </Card>)}
        </div>
      </div>
      {/* Import vs Export Trend and Country-wise Trade */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-9250">
        {/* Import vs Export Trend */}
        <Card className="p-6" data-id="element-9251">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-9252">
            Import vs Export Trend
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-9253">
            Monthly trade balance analysis (₹ Cr)
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-9254">
            <ComposedChart data={importExportTrendData} data-id="element-9255">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-9256" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-9257" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-9258" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `₹${value} Cr`} data-id="element-9259" />
              <Legend data-id="element-9260" />
              <Bar dataKey="imports" fill="#ef4444" name="Imports" data-id="element-9261" />
              <Bar dataKey="exports" fill="#10b981" name="Exports" data-id="element-9262" />
              <Line type="monotone" dataKey="netTrade" stroke="#3b82f6" strokeWidth={3} name="Net Trade Balance" dot={{
              r: 5
            }} data-id="element-9263" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-9264">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-9265">
              <div className="text-center" data-id="element-9266">
                <div className="text-gray-600" data-id="element-9267">Sep Imports</div>
                <div className="text-xl font-bold text-red-600" data-id="element-9268">₹348 Cr</div>
              </div>
              <div className="text-center" data-id="element-9269">
                <div className="text-gray-600" data-id="element-9270">Sep Exports</div>
                <div className="text-xl font-bold text-green-600" data-id="element-9271">₹175 Cr</div>
              </div>
              <div className="text-center" data-id="element-9272">
                <div className="text-gray-600" data-id="element-9273">Trade Deficit</div>
                <div className="text-xl font-bold text-blue-600" data-id="element-9274">-₹173 Cr</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Country-wise Trade Map */}
        <Card className="p-6" data-id="element-9275">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-9276">
            Country-wise Trade Map
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-9277">
            Import and export volumes by country (₹ Cr)
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-9278">
            <BarChart data={countryTradeData} layout="vertical" data-id="element-9279">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-9280" />
              <XAxis type="number" tick={{
              fontSize: 12
            }} data-id="element-9281" />
              <YAxis dataKey="country" type="category" width={80} tick={{
              fontSize: 11
            }} data-id="element-9282" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `₹${value} Cr`} data-id="element-9283" />
              <Legend data-id="element-9284" />
              <Bar dataKey="imports" fill="#ef4444" name="Imports" data-id="element-9285" />
              <Bar dataKey="exports" fill="#10b981" name="Exports" data-id="element-9286" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-9287">
            <div className="grid grid-cols-4 gap-2 text-sm" data-id="element-9288">
              <div className="text-center" data-id="element-9289">
                <div className="text-gray-600" data-id="element-9290">Top Import</div>
                <div className="text-lg font-bold text-red-600" data-id="element-9291">China</div>
                <div className="text-xs text-gray-500" data-id="element-9292">₹1,245 Cr</div>
              </div>
              <div className="text-center" data-id="element-9293">
                <div className="text-gray-600" data-id="element-9294">Top Export</div>
                <div className="text-lg font-bold text-green-600" data-id="element-9295">USA</div>
                <div className="text-xs text-gray-500" data-id="element-9296">₹385 Cr</div>
              </div>
              <div className="text-center" data-id="element-9297">
                <div className="text-gray-600" data-id="element-9298">Trade Partners</div>
                <div className="text-lg font-bold text-gray-900" data-id="element-9299">8</div>
                <div className="text-xs text-gray-500" data-id="element-9300">countries</div>
              </div>
              <div className="text-center" data-id="element-9301">
                <div className="text-gray-600" data-id="element-9302">Trade Surplus</div>
                <div className="text-lg font-bold text-green-600" data-id="element-9303">2</div>
                <div className="text-xs text-gray-500" data-id="element-9304">USA, UK</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* FX Impact Chart */}
      <Card className="p-6 mb-8" data-id="element-9305">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-9306">
          FX Impact Chart
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-9307">
          Currency rates and foreign exchange gain/loss (₹ Cr)
        </p>
        <ResponsiveContainer width="100%" height={350} data-id="element-9308">
          <ComposedChart data={fxImpactData} data-id="element-9309">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-9310" />
            <XAxis dataKey="month" tick={{
            fontSize: 12
          }} data-id="element-9311" />
            <YAxis yAxisId="left" tick={{
            fontSize: 12
          }} label={{
            value: 'Exchange Rate',
            angle: -90,
            position: 'insideLeft'
          }} domain={[0, 100]} data-id="element-9312" />
            <YAxis yAxisId="right" orientation="right" tick={{
            fontSize: 12
          }} label={{
            value: 'FX Gain/Loss (₹ Cr)',
            angle: 90,
            position: 'insideRight'
          }} domain={[-40, 0]} data-id="element-9313" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} formatter={(value: any, name: string) => {
            if (name === 'FX Gain/Loss') return `₹${value} Cr`;
            return value.toFixed(2);
          }} data-id="element-9314" />
            <Legend data-id="element-9315" />
            <Line yAxisId="left" type="monotone" dataKey="usdRate" stroke="#3b82f6" strokeWidth={2} name="USD/INR" dot={{
            r: 4
          }} data-id="element-9316" />
            <Line yAxisId="left" type="monotone" dataKey="eurRate" stroke="#10b981" strokeWidth={2} name="EUR/INR" dot={{
            r: 4
          }} data-id="element-9317" />
            <Line yAxisId="left" type="monotone" dataKey="jpyRate" stroke="#f59e0b" strokeWidth={2} name="JPY/INR (x100)" dot={{
            r: 4
          }} data-id="element-9318" />
            <Bar yAxisId="right" dataKey="fxGainLoss" fill="#ef4444" name="FX Gain/Loss" data-id="element-9319" />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-9320">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-9321">
            <div className="text-center" data-id="element-9322">
              <div className="text-gray-600" data-id="element-9323">USD/INR</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-9324">83.9</div>
              <div className="text-xs text-red-600" data-id="element-9325">+1.7% YTD</div>
            </div>
            <div className="text-center" data-id="element-9326">
              <div className="text-gray-600" data-id="element-9327">EUR/INR</div>
              <div className="text-xl font-bold text-green-600" data-id="element-9328">92.5</div>
              <div className="text-xs text-red-600" data-id="element-9329">+3.7% YTD</div>
            </div>
            <div className="text-center" data-id="element-9330">
              <div className="text-gray-600" data-id="element-9331">JPY/INR</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-9332">0.67</div>
              <div className="text-xs text-red-600" data-id="element-9333">+8.1% YTD</div>
            </div>
            <div className="text-center" data-id="element-9334">
              <div className="text-gray-600" data-id="element-9335">Sep FX Loss</div>
              <div className="text-xl font-bold text-red-600" data-id="element-9336">-₹28.8 Cr</div>
            </div>
            <div className="text-center" data-id="element-9337">
              <div className="text-gray-600" data-id="element-9338">YTD FX Loss</div>
              <div className="text-xl font-bold text-red-600" data-id="element-9339">-₹180.8 Cr</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Import/Export SKU/Commodity Listing Table */}
      <Card className="p-6" data-id="element-9340">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-9341">
          Import/Export SKU/Commodity Listing - Detailed Analysis
        </h3>
        <div className="overflow-x-auto" data-id="element-9342">
          <table className="w-full" data-id="element-9343">
            <thead data-id="element-9344">
              <tr className="border-b-2 border-gray-300" data-id="element-9345">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9346">
                  SKU Code
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9347">
                  Description
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9348">
                  Type
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9349">
                  Category
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9350">
                  Country
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9351">
                  Partner
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9352">
                  Currency
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9353">
                  Unit Price
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9354">
                  Volume
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9355">
                  Value (FC)
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9356">
                  FX Rate
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9357">
                  INR Value
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9358">
                  Lead Time
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9359">
                  HS Code
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9360">
                  Duty %
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9361">
                  Customs Duty
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9362">
                  Total Cost
                </th>
              </tr>
            </thead>
            <tbody data-id="element-9363">
              {importExportListingData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-9364">
                  <td className="py-3 px-4 text-sm font-mono text-blue-600" data-id="element-9365">
                    {row.skuCode}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-800" data-id="element-9366">
                    {row.description}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-9367">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getTypeColor(row.type)}`} data-id="element-9368">
                      {row.type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-9369">
                    {row.category}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-9370">
                    {row.country}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-9371">
                    {row.type === 'Import' ? row.supplier : row.customer}
                  </td>
                  <td className="py-3 px-4 text-center text-sm font-semibold text-gray-900" data-id="element-9372">
                    {row.currency}
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-gray-700" data-id="element-9373">
                    {row.unitPrice.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-gray-700" data-id="element-9374">
                    {row.volume.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right text-sm font-medium text-blue-600" data-id="element-9375">
                    {row.value.toFixed(2)}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-9376">
                    {row.fxRate.toFixed(2)}
                  </td>
                  <td className="py-3 px-4 text-right text-sm font-semibold text-gray-900" data-id="element-9377">
                    ₹{row.inrValue.toFixed(1)} Cr
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-9378">
                    {row.leadTime}d
                  </td>
                  <td className="py-3 px-4 text-sm font-mono text-gray-600" data-id="element-9379">
                    {row.hscode}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-9380">
                    {row.dutyRate.toFixed(1)}%
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-orange-600" data-id="element-9381">
                    {row.customsDuty > 0 ? `₹${row.customsDuty.toFixed(1)} Cr` : '-'}
                  </td>
                  <td className="py-3 px-4 text-right text-sm font-bold text-gray-900" data-id="element-9382">
                    ₹{row.totalCost.toFixed(1)} Cr
                  </td>
                </tr>)}
              {/* Summary Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold" data-id="element-9383">
                <td className="py-3 px-4 text-sm text-gray-900" colSpan={8} data-id="element-9384">
                  TOTAL
                </td>
                <td className="py-3 px-4 text-right text-sm text-gray-900" data-id="element-9385">
                  90,800
                </td>
                <td className="py-3 px-4 text-right text-sm text-blue-600" data-id="element-9386">
                  2,624.7
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-9387">
                  -
                </td>
                <td className="py-3 px-4 text-right text-sm text-gray-900" data-id="element-9388">
                  ₹2,172 Cr
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-9389">
                  -
                </td>
                <td className="py-3 px-4 text-sm text-gray-700" data-id="element-9390">-</td>
                <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-9391">
                  -
                </td>
                <td className="py-3 px-4 text-right text-sm text-orange-600" data-id="element-9392">
                  ₹114.5 Cr
                </td>
                <td className="py-3 px-4 text-right text-sm text-gray-900" data-id="element-9393">
                  ₹2,287 Cr
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-9394">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-9395">
            <div className="text-center" data-id="element-9396">
              <div className="text-gray-600" data-id="element-9397">Total Items</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-9398">10</div>
            </div>
            <div className="text-center" data-id="element-9399">
              <div className="text-gray-600" data-id="element-9400">Imports</div>
              <div className="text-xl font-bold text-red-600" data-id="element-9401">6</div>
            </div>
            <div className="text-center" data-id="element-9402">
              <div className="text-gray-600" data-id="element-9403">Exports</div>
              <div className="text-xl font-bold text-green-600" data-id="element-9404">4</div>
            </div>
            <div className="text-center" data-id="element-9405">
              <div className="text-gray-600" data-id="element-9406">Total Volume</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-9407">90,800</div>
            </div>
            <div className="text-center" data-id="element-9408">
              <div className="text-gray-600" data-id="element-9409">Customs Duty</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-9410">₹114.5 Cr</div>
            </div>
            <div className="text-center" data-id="element-9411">
              <div className="text-gray-600" data-id="element-9412">Total Value</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-9413">₹2,287 Cr</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default ImportExportTrackerDashboard;